import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useCaloriesByPlacementAndPortionText } from '../../../../hooks/pizzaIngredientOptionsSelectors';
import Placement from '../../../../../../common/Placement';
import Portion from '../../../../../../common/Portion';
import ToppingItem from '../ToppingItem';
import {
  PlacementChoice,
  PizzaIngredient,
  PizzaIngredientOption
} from '@/builders/pizza/dataTransformers/builderTypes';
import { selectors } from '@/builders/pizza/slice/pizza.slice';

export interface OnToppingItemChangeProps {
  hasExtra: boolean;
  placement: PlacementChoice;
  isSelected: boolean;
}

interface ToppingItemOptimizedProps {
  topping: PizzaIngredientOption;
  selectedIngredient: PizzaIngredient | undefined;
  extraPrice: number | undefined | null;
  onChange: (
    topping: PizzaIngredientOption,
    onToppingItemChange: OnToppingItemChangeProps
  ) => void;
  reachedToppingLimit?: boolean;
  reachedPanCrustLimit?: boolean;
  reachedBNYCrustLimit?: boolean;
  itemType?: 'topping' | 'finisher';
}

const placementWeights = ["LEFT_REGULAR", "LEFT_EXTRA", "RIGHT_REGULAR", "RIGHT_EXTRA"];
const placementExcludedFromVariant = (topping: PizzaIngredientOption, variant: string | undefined) => {
  if (!variant) return false;
  return topping.weights?.filter(weight => placementWeights.includes(weight)).some(weight => 
    topping.excludedVariants?.[weight]?.includes(variant)
  );
}

const getPlacementAvailable = ({ isPlacementExcludedFromVariant, splittable }: { isPlacementExcludedFromVariant?: boolean; splittable?: boolean }) => {
  if (isPlacementExcludedFromVariant) return false;
  return splittable;
};

const ToppingItemOptimized = ({
  topping,
  selectedIngredient,
  extraPrice,
  onChange,
  reachedToppingLimit,
  reachedPanCrustLimit,
  reachedBNYCrustLimit,
  itemType = 'topping'
}: ToppingItemOptimizedProps): JSX.Element => {
  const {
    splittable, name, outOfStock, id, image, maxAllowed, weights
  } = topping;
  const nutrition = useCaloriesByPlacementAndPortionText(
    topping,
    selectedIngredient
  );

  const currentCrust = useSelector(selectors.selectPizzaCrustOption);

  const yumToppingExtraAllowed = !!(weights?.length && weights.find((weight) => weight.includes('EXTRA'))) || false;
  const isPlacementExcludedFromVariant = placementExcludedFromVariant(topping, currentCrust?.variantCode);

  const itemProps = {
    id,
    name,
    imgUrl: image,
    isOutOfStock: outOfStock,
    isSelected: selectedIngredient !== undefined,
    placementSelected: selectedIngredient?.placement ?? Placement.WHOLE,
    placementAvailable: getPlacementAvailable({
      isPlacementExcludedFromVariant,
      splittable
    }),
    extraAvailable: (maxAllowed && maxAllowed > 1) || yumToppingExtraAllowed,
    extraSelected: selectedIngredient?.portion === Portion.EXTRA,
    extraPrice,
    nutrition,
    reachedToppingLimit,
    reachedPanCrustLimit,
    reachedBNYCrustLimit
  };

  const onChangeCallback = useCallback((event:any) => onChange(topping, event), [
    onChange,
    topping
  ]);

  return (<ToppingItem {...itemProps} onChange={onChangeCallback} itemType={itemType} />);
};

export default ToppingItemOptimized;
